/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {dlsColor} from '../../../theme/theme';

export interface BreadcrumbItemProps {
  label: string;
  isActive?: boolean;
  onClick?: () => void;
}

const useStyles = makeStyles(() => ({
  // Inter, which has its own italic - the theme's 'Roboto' (Archivo Narrow)
  // has none, so its ancestor crumbs were drawn in a slanted fallback ...
  item : {
    fontFamily    : 'Inter',
    fontSize      : '14px',
    lineHeight    : '16px',
    letterSpacing : '0.5px',
    color         : dlsColor('linkBlue'),
    whiteSpace    : 'nowrap',
    // a label longer than its line is truncated with an ellipsis (full
    // label available via `title`) rather than overflowing the header ...
    minWidth      : 0,
    maxWidth      : '100%',
    overflow      : 'hidden',
    textOverflow  : 'ellipsis'
  },
  inactive : {
    flexShrink : 0,
    fontStyle  : 'italic',
    fontWeight : 600,
    cursor     : 'pointer'
  },
  active : {
    flexShrink : 1,
    fontStyle  : 'normal',
    fontWeight : 600,
    cursor     : 'default'
  }
}));

/**
 * BreadcrumbItem renders a single crumb in the Drawer Header's breadcrumb
 * trail - an italic, clickable label for ancestor levels, and a bold,
 * non-clickable label for the current (active) level. Per the Side Panel
 * Header Figma spec, both states share the same `linkBlue` (#276AD8) text
 * color and only differ in font-style/weight/cursor.
 *
 * ```typescript
 *
 *   import {BreadcrumbItem} from
 *     '@ion/dls/src/components/drawer/current/BreadcrumbItem';
 *   ...
 *
 *   <BreadcrumbItem label='Vehicle 13 Vision'
 *     isActive
 *     onClick={onCrumbClick} />
 *
 * ```
 *
 */
export const BreadcrumbItem = memo(({
  label,
  isActive = false,
  onClick
}: BreadcrumbItemProps) => {
  const classes = useStyles();

  const onItemClick = useCallback(function() {
    if (!isActive) {
      onClick?.();
    }
  }, [isActive, onClick]);

  return (
    <span className={classnames(classes.item, isActive ? classes.active : classes.inactive)}
      onClick={!isActive && onClick ? onItemClick : undefined}
      role={!isActive && onClick ? 'button' : undefined}
      tabIndex={!isActive && onClick ? 0 : undefined}
      title={label}>
      {label}
    </span>
  );
});

export default BreadcrumbItem;
